import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Chip,
  LinearProgress,
} from '@mui/material';
import { CloudUpload, Lock, Delete, FileDownload } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import { productService } from '../services/productService';

const CATEGORIES = [
  'icons',
  'logos',
  'illustrations',
  'graphics',
  'images',
  'ebooks',
  'software',
  'templates',
  'music',
  'videos',
  'courses',
  'other'
];

interface AdminUploadProps {
  user: User | null;
  onRequireLogin?: () => void;
}

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  category: string;
  tags: string;
  previewImages: File[];
  previewPreviews: string[];
  downloadFile: File | null;
  downloadPreview: string | null;
}

interface UploadProgress {
  loaded: number;
  total: number;
}

const AdminUpload: React.FC<AdminUploadProps> = ({ user, onRequireLogin }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [createdProductId, setCreatedProductId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: '',
    category: 'images',
    tags: '',
    previewImages: [],
    previewPreviews: [],
    downloadFile: null,
    downloadPreview: null,
  });

  // Cleanup object URLs on unmount (must be before early returns)
  useEffect(() => {
    return () => {
      formData.previewPreviews.forEach(url => URL.revokeObjectURL(url));
      if (formData.downloadPreview) {
        URL.revokeObjectURL(formData.downloadPreview);
      }
    };
  }, [formData.previewPreviews, formData.downloadPreview]);

  // Check authentication and admin role
  useEffect(() => {
    if (!user) {
      // Not logged in - redirect or show login prompt
      if (onRequireLogin) {
        onRequireLogin();
      }
      navigate('/');
      return;
    }

    if (user.role !== 'admin') {
      // Not an admin - show error
      setError('Access denied. Admin privileges required.');
    }
  }, [user, navigate, onRequireLogin]);

  // Don't render if not admin
  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Lock sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Please sign in to access this page
        </Typography>
      </Box>
    );
  }

  if (user.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You must be an administrator to access this page.
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </Box>
    );
  }

  const handlePreviewImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const previews: string[] = [];

    files.forEach(file => {
      // Validate file type (previews must be images)
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type for ${file.name}. Preview files must be images (JPEG, PNG, GIF, SVG, or WebP)`);
        return;
      }

      // Validate file size (max 20MB per preview image)
      if (file.size > 20 * 1024 * 1024) {
        setError(`File ${file.name} is too large. Maximum size for preview images is 20MB`);
        return;
      }

      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    });

    if (validFiles.length > 0) {
      setFormData({
        ...formData,
        previewImages: [...formData.previewImages, ...validFiles],
        previewPreviews: [...formData.previewPreviews, ...previews],
      });
      setError(null);
    }

    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleDownloadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 500MB for download files)
      if (file.size > 500 * 1024 * 1024) {
        setError('Download file size too large. Maximum size is 500MB');
        return;
      }

      setFormData({
        ...formData,
        downloadFile: file,
        downloadPreview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
      setError(null);
    }
  };

  const removePreviewImage = (index: number) => {
    // Revoke object URL to prevent memory leaks
    URL.revokeObjectURL(formData.previewPreviews[index]);
    
    const newImages = formData.previewImages.filter((_, i) => i !== index);
    const newPreviews = formData.previewPreviews.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      previewImages: newImages,
      previewPreviews: newPreviews,
    });
  };

  const removeDownloadFile = () => {
    if (formData.downloadPreview) {
      URL.revokeObjectURL(formData.downloadPreview);
    }
    setFormData({
      ...formData,
      downloadFile: null,
      downloadPreview: null,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCategoryChange = (e: any) => {
    setFormData({
      ...formData,
      category: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(null);

    try {
      // Validate form
      if (!formData.name || !formData.description || !formData.price) {
        throw new Error('Please fill in all required fields (name, description, price)');
      }

      if (formData.previewImages.length === 0) {
        throw new Error('Please upload at least one preview image');
      }

      if (!formData.downloadFile) {
        throw new Error('Please upload a download file');
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        throw new Error('Please enter a valid price');
      }

      // Step 1: Initialize presigned upload - get presigned URLs
      const initResponse = await productService.initPresignedUpload({
        name: formData.name,
        description: formData.description,
        price: formData.price,
        category: formData.category,
        tags: formData.tags,
        previewImages: formData.previewImages.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type
        })),
        downloadFile: {
          name: formData.downloadFile.name,
          size: formData.downloadFile.size,
          type: formData.downloadFile.type
        }
      });

      if (!initResponse.success || !initResponse.data) {
        throw new Error(initResponse.message || 'Failed to initialize upload');
      }

      const { productId, presignedUrls } = initResponse.data;

      // Step 2: Upload files directly to S3 using presigned URLs
      let totalUploaded = 0;
      const totalSize = formData.previewImages.reduce((sum, file) => sum + file.size, 0) + formData.downloadFile.size;

      // Upload preview images
      for (let i = 0; i < formData.previewImages.length; i++) {
        const file = formData.previewImages[i];
        const presignedUrl = presignedUrls.previewImages[i];

        try {
          await productService.uploadFileToS3(
            presignedUrl.url,
            file,
            (progress) => {
              // Calculate overall progress including all files
              const fileProgress = (progress.loaded / progress.total) * file.size;
              totalUploaded = formData.previewImages.slice(0, i).reduce((sum, f) => sum + f.size, 0) + fileProgress;
              setUploadProgress({
                loaded: totalUploaded,
                total: totalSize
              });
            }
          );
          console.log(`✅ Successfully uploaded preview image ${i + 1}/${formData.previewImages.length}: ${file.name}`);
        } catch (uploadError: any) {
          console.error(`❌ Failed to upload preview image ${i + 1}: ${file.name}`, uploadError);
          throw new Error(`Failed to upload preview image "${file.name}": ${uploadError.message}`);
        }
      }

      // Upload download file
      try {
        await productService.uploadFileToS3(
          presignedUrls.downloadFile.url,
          formData.downloadFile,
          (progress) => {
            const previewsSize = formData.previewImages.reduce((sum, file) => sum + file.size, 0);
            totalUploaded = previewsSize + progress.loaded;
            setUploadProgress({
              loaded: totalUploaded,
              total: totalSize
            });
          }
        );
        console.log(`✅ Successfully uploaded download file: ${formData.downloadFile.name}`);
      } catch (uploadError: any) {
        console.error(`❌ Failed to upload download file: ${formData.downloadFile.name}`, uploadError);
        throw new Error(`Failed to upload download file "${formData.downloadFile.name}": ${uploadError.message}`);
      }

      // Step 3: Finalize upload - verify files and create product
      const finalizeResponse = await productService.finalizePresignedUpload(productId, {
        previewImages: presignedUrls.previewImages.map((presigned, index) => ({
          key: presigned.key,
          originalName: formData.previewImages[index].name
        })),
        downloadFile: {
          key: presignedUrls.downloadFile.key,
          originalName: formData.downloadFile.name
        }
      });

      if (!finalizeResponse.success) {
        throw new Error(finalizeResponse.message || 'Failed to finalize upload');
      }

      // Success!
      const productIdFromResponse = finalizeResponse.data?.id || finalizeResponse.data?._id || productId;
      setCreatedProductId(productIdFromResponse);
      setSuccess(`Product "${formData.name}" uploaded successfully with ${formData.previewImages.length} preview image(s)!`);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        price: '',
        category: 'images',
        tags: '',
        previewImages: [],
        previewPreviews: [],
        downloadFile: null,
        downloadPreview: null,
      });
      setUploadProgress(null);
      
      // Navigate to the product page after a short delay to show success message
      setTimeout(() => {
        navigate(`/listing/${productIdFromResponse}`);
      }, 2000);
    } catch (err: any) {
      console.error('Upload error:', err);
      
      // Provide more helpful error messages
      let errorMessage = err.message || 'An error occurred while uploading the product';
      
      // Check for CORS errors
      if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
        errorMessage = 'CORS Error: Your S3 bucket is not configured for browser uploads. Please run: cd backend && node scripts/configure-s3-cors.js';
      } else if (errorMessage.includes('Network error')) {
        errorMessage = 'Network Error: Failed to upload files to S3. This may be a CORS configuration issue. Please check your S3 bucket CORS settings.';
      } else if (errorMessage.includes('not found in S3')) {
        errorMessage = 'Upload Error: Files were not uploaded to S3. This is likely due to CORS blocking the uploads. Please configure CORS on your S3 bucket.';
      }
      
      setError(errorMessage);
      setUploadProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Cleanup object URLs
    formData.previewPreviews.forEach(url => URL.revokeObjectURL(url));
    if (formData.downloadPreview) {
      URL.revokeObjectURL(formData.downloadPreview);
    }

    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'images',
      tags: '',
      previewImages: [],
      previewPreviews: [],
      downloadFile: null,
      downloadPreview: null,
    });
    setError(null);
    setSuccess(null);
    setUploadProgress(null);
    setCreatedProductId(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, pt: { xs: 4, md: 5 } }}>
      <Typography variant="h4" gutterBottom>
        Admin: Upload New Product
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload new images and products to the marketplace
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }} 
          onClose={() => {
            setSuccess(null);
            setCreatedProductId(null);
          }}
          action={
            createdProductId ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate(`/listing/${createdProductId}`)}
              >
                View Product
              </Button>
            ) : null
          }
        >
          {success}
        </Alert>
      )}

      {uploadProgress && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={(uploadProgress.loaded / uploadProgress.total) * 100} 
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" align="center">
            Uploading... {Math.round((uploadProgress.loaded / uploadProgress.total) * 100)}%
          </Typography>
        </Box>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Preview Images Upload Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Preview Images {formData.previewImages.length > 0 && `(${formData.previewImages.length})`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload multiple preview images (at least 1 required)
              </Typography>
              
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  mb: 2,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="preview-images-upload"
                  type="file"
                  multiple
                  onChange={handlePreviewImagesChange}
                />
                <label htmlFor="preview-images-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    sx={{ mb: 1 }}
                  >
                    Add Preview Images
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary" display="block">
                  JPEG, PNG, GIF, SVG, WebP (Max 20MB each)
                </Typography>
              </Box>

              {/* Preview Images Thumbnails */}
              {formData.previewImages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {formData.previewImages.map((file, index) => (
                      <Grid item xs={6} sm={4} key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={formData.previewPreviews[index]}
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bgcolor: 'rgba(0,0,0,0.5)',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => removePreviewImage(index)}
                              sx={{ color: 'white' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" noWrap>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Download File Upload Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Download File {formData.downloadFile && <Chip label="Selected" size="small" color="primary" />}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload the file customers will download (required)
              </Typography>
              
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <input
                  style={{ display: 'none' }}
                  id="download-file-upload"
                  type="file"
                  onChange={handleDownloadFileChange}
                />
                <label htmlFor="download-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<FileDownload />}
                    sx={{ mb: 1 }}
                  >
                    {formData.downloadFile ? 'Change Download File' : 'Choose Download File'}
                  </Button>
                </label>
                
                {formData.downloadFile ? (
                  <Box sx={{ mt: 2 }}>
                    {formData.downloadPreview ? (
                      <img
                        src={formData.downloadPreview}
                        alt="Download preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '8px',
                        }}
                      />
                    ) : (
                      <FileDownload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {formData.downloadFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(formData.downloadFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        startIcon={<Delete />}
                        onClick={removeDownloadFile}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <FileDownload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Click to select download file
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Any file type (Max 500MB)
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Product Details Section */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                multiline
                rows={4}
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      onChange={handleCategoryChange}
                      label="Category"
                    >
                      {CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Tags (comma-separated)"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="e.g., design, vector, modern"
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
                >
                  {loading ? 'Uploading...' : 'Upload Product'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AdminUpload;

